// v4
// Path: frontend/src/components/QuickBooksSync/FieldMappingModal.jsx
// Inputs: token, config, onClose, onSaved
// Outputs: React JSX modal allowing visual assignment, composite column mapping, live table switching, live sample preview, and saving of dynamic field mappings
// Summary: Interactive field mapping designer with live connected database table switcher, live column introspection, searchable column dropdowns, composite column assignment, and live sample preview.

import React, { useState, useEffect, useCallback } from 'react';
import SearchableSelect from './SearchableSelect';

export const FieldMappingModal = ({ token, config, onClose, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');
    
    // Connected Table and QB Entity state
    const [selectedQBEntity, setSelectedQBEntity] = useState(config.TargetQBEntity || config.EntityType || 'Customer');
    const [selectedTable, setSelectedTable] = useState(config.TableName || '');
    const [availableTables, setAvailableTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);

    // Live sample data state
    const [sampleRecords, setSampleRecords] = useState([]);
    const [loadingSample, setLoadingSample] = useState(false);
    const [activeSampleIndex, setActiveSampleIndex] = useState(0);
    const [showSampleDrawer, setShowSampleDrawer] = useState(false);

    // New custom mapping inputs
    const [newTarget, setNewTarget] = useState('');
    const [newSource, setNewSource] = useState('');

    const qbEntities = [
        'Customer', 'Vendor', 'Item', 'Account', 'Invoice', 'Bill', 'Payment', 'JournalEntry'
    ];

    // Compute default table name if not provided
    const getDefaultTable = (ent, db) => {
        const match = (db || '').trim().match(/(\d{2})$/);
        const suffix = match ? match[1] : '00';
        const clean = (ent || '').toLowerCase();
        if (clean.includes('cust')) return `CUSFIL${suffix}`;
        if (clean.includes('item') || clean.includes('inv')) return `ITMFIL${suffix}`;
        if (clean.includes('account') || clean.includes('chart')) return `CHARTF${suffix}`;
        if (clean.includes('vend')) return `VENFIL${suffix}`;
        if (clean.includes('order')) return `OEHDR${suffix}`;
        return `CUSFIL${suffix}`;
    };

    const activeTableName = selectedTable || config.TableName || getDefaultTable(config.EntityType, config.SourceDatabaseName);

    // 1. Fetch available tables in the connected database
    useEffect(() => {
        const fetchTables = async () => {
            if (!config.SourceConnPrefix || !config.SourceDatabaseName) return;
            setLoadingTables(true);
            try {
                const res = await fetch(`/api/quickbooks/database-tables?connPrefix=${encodeURIComponent(config.SourceConnPrefix)}&databaseName=${encodeURIComponent(config.SourceDatabaseName)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success && Array.isArray(data.tables)) {
                    setAvailableTables(data.tables);
                }
            } catch (err) {
                console.warn('[FieldMappingModal] Error fetching database tables:', err);
            } finally {
                setLoadingTables(false);
            }
        };
        fetchTables();
    }, [config.SourceConnPrefix, config.SourceDatabaseName, token]);

    // 2. Load schema definitions and sample records
    const loadSchemaAndSamples = useCallback(async (tableToUse, qbEntityToUse) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (config.SourceConnPrefix) params.append('connPrefix', config.SourceConnPrefix);
            if (config.SourceDatabaseName) params.append('sourceDatabaseName', config.SourceDatabaseName);
            if (tableToUse) params.append('tableName', tableToUse);

            // Fetch Field Definitions (live introspection from SQL Server)
            const res = await fetch(`/api/quickbooks/field-definitions/${qbEntityToUse || config.EntityType}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                setDefinitions(data);

                // Update selected table if backend resolved it
                if (!selectedTable && data.tableName) {
                    setSelectedTable(data.tableName);
                }

                // Load existing mappings or fallback to defaults
                let initialMappings = [];
                if (config.FieldMappingsJson) {
                    try {
                        const parsed = typeof config.FieldMappingsJson === 'string' ? JSON.parse(config.FieldMappingsJson) : config.FieldMappingsJson;
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            initialMappings = parsed.map(m => ({
                                ...m,
                                sources: Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []),
                                delimiter: m.delimiter !== undefined ? m.delimiter : '-'
                            }));
                        }
                    } catch (e) {
                        console.warn('Could not parse FieldMappingsJson:', e);
                    }
                }

                if (initialMappings.length === 0) {
                    initialMappings = (data.defaultMappings || []).map(m => ({
                        ...m,
                        sources: Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []),
                        delimiter: m.delimiter !== undefined ? m.delimiter : '-'
                    }));
                }

                setMappings(initialMappings);
            }

            // Fetch Live Sample Records from the active table
            if (config.SourceDatabaseName && tableToUse) {
                setLoadingSample(true);
                try {
                    const sampleUrl = `/api/quickbooks/pbs-preview?connPrefix=${encodeURIComponent(config.SourceConnPrefix || '')}&sourceDatabaseName=${encodeURIComponent(config.SourceDatabaseName)}&tableName=${encodeURIComponent(tableToUse)}&entityType=${encodeURIComponent(config.EntityType || 'Customers')}&limit=10`;
                    const sampleRes = await fetch(sampleUrl, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (sampleRes.ok) {
                        const sData = await sampleRes.json();
                        setSampleRecords(Array.isArray(sData.records) ? sData.records : (Array.isArray(sData) ? sData : []));
                        setActiveSampleIndex(0);
                    } else {
                        setSampleRecords([]);
                    }
                } catch (sErr) {
                    console.warn('[FieldMappingModal] Could not fetch sample data:', sErr);
                    setSampleRecords([]);
                } finally {
                    setLoadingSample(false);
                }
            }
        } catch (err) {
            console.error('[FieldMappingModal] Error loading definitions:', err);
        } finally {
            setLoading(false);
        }
    }, [config, selectedTable, token]);

    // Initial load
    useEffect(() => {
        loadSchemaAndSamples(activeTableName, selectedQBEntity);
    }, [activeTableName, selectedQBEntity, loadSchemaAndSamples]);

    // Handle user switching connected table
    const handleSwitchTable = (newTable) => {
        setSelectedTable(newTable);
        loadSchemaAndSamples(newTable, selectedQBEntity);
    };

    // Handle user switching target QB entity
    const handleSwitchQBEntity = (newEntity) => {
        setSelectedQBEntity(newEntity);
        loadSchemaAndSamples(activeTableName, newEntity);
    };

    // Calculate live sample evaluation for a mapping
    const getLivePreviewValue = (mapping) => {
        if (sampleRecords.length === 0) return '(No samples)';
        const currentSample = sampleRecords[activeSampleIndex] || sampleRecords[0];
        if (!currentSample) return '(No sample)';

        const cols = Array.isArray(mapping.sources) && mapping.sources.length > 0
            ? mapping.sources
            : (mapping.source ? [mapping.source] : []);

        if (cols.length === 0) return '(Not assigned)';

        const getColVal = (cName) => {
            if (currentSample[cName] !== undefined) return currentSample[cName];
            const lower = cName.toLowerCase();
            for (const k of Object.keys(currentSample)) {
                if (k.toLowerCase() === lower) return currentSample[k];
            }
            return '';
        };

        const parts = cols.map(c => String(getColVal(c) ?? '').trim()).filter(Boolean);
        if (parts.length === 0) return '(Empty in sample)';
        return parts.join(mapping.delimiter || '-');
    };

    // Toggle mapping enabled
    const handleToggleMapping = (index) => {
        setMappings(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], enabled: !copy[index].enabled };
            return copy;
        });
    };

    // Add source column to composite mapping
    const handleAddSourceColumn = (index, colName) => {
        if (!colName) return;
        setMappings(prev => {
            const copy = [...prev];
            const currentSources = copy[index].sources || (copy[index].source ? [copy[index].source] : []);
            if (!currentSources.includes(colName)) {
                const updated = [...currentSources, colName];
                copy[index] = {
                    ...copy[index],
                    sources: updated,
                    source: updated.join(copy[index].delimiter || '-')
                };
            }
            return copy;
        });
    };

    // Remove source column from composite mapping
    const handleRemoveSourceColumn = (index, colToRemove) => {
        setMappings(prev => {
            const copy = [...prev];
            const currentSources = copy[index].sources || [];
            const updated = currentSources.filter(c => c !== colToRemove);
            copy[index] = {
                ...copy[index],
                sources: updated,
                source: updated.join(copy[index].delimiter || '-')
            };
            return copy;
        });
    };

    // Change delimiter for composite mapping
    const handleDelimiterChange = (index, delimiter) => {
        setMappings(prev => {
            const copy = [...prev];
            copy[index] = {
                ...copy[index],
                delimiter,
                source: (copy[index].sources || []).join(delimiter)
            };
            return copy;
        });
    };

    const handleRemoveMapping = (index) => {
        setMappings(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddCustomMapping = () => {
        if (!newTarget || !newSource) return;
        setMappings(prev => [
            ...prev,
            {
                target: newTarget,
                source: newSource,
                sources: [newSource],
                delimiter: '-',
                enabled: true
            }
        ]);
        setNewTarget('');
        setNewSource('');
    };

    const handleResetDefaults = () => {
        if (definitions?.defaultMappings) {
            setMappings(definitions.defaultMappings.map(m => ({
                ...m,
                sources: Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []),
                delimiter: m.delimiter !== undefined ? m.delimiter : '-'
            })));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                configId: config.ConfigID,
                tableName: activeTableName,
                targetQBEntity: selectedQBEntity,
                fieldMappingsJson: JSON.stringify(mappings)
            };

            const res = await fetch('/api/quickbooks/configs/field-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save field mappings');
            }

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            alert(`Error saving mappings: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const sourceFields = definitions?.sourceFields || [];
    const targetFields = definitions?.targetFields || [];

    // Format source options for SearchableSelect
    const sourceSelectOptions = sourceFields.map(sf => ({
        value: sf.name,
        label: sf.name,
        description: sf.description || sf.label || '',
        cobolName: sf.cobolName || '',
        sqlType: sf.sqlType || '',
        maxLength: sf.maxLength || '',
        isPrimaryKey: !!sf.isPrimaryKey,
        isLiveColumn: !!sf.isLiveColumn
    }));

    const filteredMappings = mappings.filter(m => {
        if (!searchFilter.trim()) return true;
        const q = searchFilter.toLowerCase();
        const tMatch = (m.target || '').toLowerCase().includes(q);
        const sMatch = (m.sources || [m.source]).some(s => (s || '').toLowerCase().includes(q));
        return tMatch || sMatch;
    });

    const activeSample = sampleRecords[activeSampleIndex] || null;

    return (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-3 sm:p-6 overflow-hidden">
            <div className="bg-slate-900 border border-slate-750 rounded-2xl w-full max-w-6xl h-[92vh] flex flex-col shadow-2xl overflow-hidden">
                
                {/* Top Header Bar */}
                <div className="p-4 border-b border-slate-800 bg-slate-950 flex flex-wrap items-center justify-between gap-3 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
                            <span className="material-symbols-outlined text-xl">conversion_path</span>
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                <span>Field Mapping Designer</span>
                                <span className="text-slate-500 font-normal text-xs">&bull;</span>
                                <span className="text-sky-300 font-mono text-xs">{config.ConfigName}</span>
                            </h3>
                            <div className="text-xs text-slate-400 flex items-center gap-2 flex-wrap mt-0.5">
                                <span>Source Profile: <strong className="text-slate-200">{config.SourceConnPrefix || 'Default'}</strong></span>
                                <span>&bull;</span>
                                <span>Database: <strong className="text-emerald-400 font-mono">{config.SourceDatabaseName}</strong></span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleResetDefaults}
                            className="px-3 py-1.5 text-xs text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors flex items-center gap-1.5"
                        >
                            <span className="material-symbols-outlined text-[15px]">restart_alt</span>
                            <span>Reset Defaults</span>
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
                        >
                            <span className="material-symbols-outlined text-xl">close</span>
                        </button>
                    </div>
                </div>

                {/* Prominent Connected Table & Live Connection Banner */}
                <div className="bg-slate-950/90 border-b border-slate-800 px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs shrink-0">
                    {/* Left: Source Table Selector with Live Dropdown */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-slate-400 font-semibold uppercase tracking-wider text-[11px] flex items-center gap-1">
                            <span className="material-symbols-outlined text-blue-400 text-[15px]">table_rows</span>
                            Connected Source Table:
                        </span>

                        {availableTables.length > 0 ? (
                            <select
                                value={activeTableName}
                                onChange={(e) => handleSwitchTable(e.target.value)}
                                className="bg-slate-900 border border-sky-500/50 rounded-lg px-2.5 py-1 text-sky-300 font-mono font-bold text-xs focus:outline-none focus:border-sky-400 cursor-pointer"
                            >
                                {availableTables.map(tbl => (
                                    <option key={tbl} value={tbl}>{tbl}</option>
                                ))}
                            </select>
                        ) : (
                            <span className="bg-slate-900 px-2.5 py-1 rounded font-mono font-bold text-sky-300 border border-slate-750">
                                {activeTableName}
                            </span>
                        )}

                        {/* Live Introspection Status Pill */}
                        {definitions?.isLive ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[11px] font-semibold">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                Live Connected ({sourceFields.length} columns)
                            </span>
                        ) : definitions?.connectionError ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[11px] font-semibold" title={definitions.connectionError}>
                                ⚠️ DB Connection Failed (Showing Dictionary)
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[11px]">
                                ℹ️ {sourceFields.length} columns from schema
                            </span>
                        )}
                    </div>

                    {/* Right: Target QuickBooks Entity Switcher & Sample Record Switcher */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                            <span className="text-slate-400 font-semibold uppercase tracking-wider text-[11px]">
                                Target QB Entity:
                            </span>
                            <select
                                value={selectedQBEntity}
                                onChange={(e) => handleSwitchQBEntity(e.target.value)}
                                className="bg-slate-900 border border-purple-500/40 rounded-lg px-2.5 py-1 text-purple-300 font-semibold text-xs focus:outline-none focus:border-purple-400 cursor-pointer"
                            >
                                {qbEntities.map(ent => (
                                    <option key={ent} value={ent}>{ent}</option>
                                ))}
                            </select>
                        </div>

                        {/* Sample Records Navigator */}
                        <div className="flex items-center gap-1.5 pl-3 border-l border-slate-800">
                            {loadingSample ? (
                                <span className="text-slate-400 text-[11px] flex items-center gap-1">
                                    <div className="w-2.5 h-2.5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin"></div>
                                    Loading samples...
                                </span>
                            ) : sampleRecords.length > 0 ? (
                                <div className="flex items-center gap-1.5 bg-slate-900 px-2 py-0.5 rounded-lg border border-slate-750">
                                    <span className="text-slate-400 text-[11px]">
                                        Sample <strong className="text-sky-300">{activeSampleIndex + 1}</strong> of {sampleRecords.length}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={activeSampleIndex === 0}
                                        onClick={() => setActiveSampleIndex(prev => Math.max(0, prev - 1))}
                                        className="text-slate-400 hover:text-white disabled:opacity-30 px-1"
                                    >
                                        ◀
                                    </button>
                                    <button
                                        type="button"
                                        disabled={activeSampleIndex >= sampleRecords.length - 1}
                                        onClick={() => setActiveSampleIndex(prev => Math.min(sampleRecords.length - 1, prev + 1))}
                                        className="text-slate-400 hover:text-white disabled:opacity-30 px-1"
                                    >
                                        ▶
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowSampleDrawer(!showSampleDrawer)}
                                        className="text-sky-400 hover:text-sky-300 text-[11px] font-semibold underline ml-1"
                                    >
                                        {showSampleDrawer ? 'Hide Raw' : 'View Raw'}
                                    </button>
                                </div>
                            ) : (
                                <span className="text-slate-500 text-[11px] italic">
                                    No live samples found in {activeTableName}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Collapsible Raw Sample Record Drawer */}
                {showSampleDrawer && activeSample && (
                    <div className="bg-slate-950 border-b border-slate-800 p-3 max-h-40 overflow-y-auto text-xs shrink-0 animate-in slide-in-from-top-2 duration-150">
                        <div className="flex justify-between items-center mb-1">
                            <span className="font-bold text-slate-300 text-[11px] uppercase tracking-wider">
                                Live Record #{activeSampleIndex + 1} ({activeTableName}) - Raw Database Payload:
                            </span>
                            <span className="text-slate-500 font-mono text-[10px]">
                                {Object.keys(activeSample).length} fields present
                            </span>
                        </div>
                        <pre className="text-slate-300 font-mono text-[11px] bg-slate-900 p-2 rounded border border-slate-800 overflow-x-auto">
                            {JSON.stringify(activeSample, null, 2)}
                        </pre>
                    </div>
                )}

                {/* Search / Filter Toolbar */}
                <div className="p-3 bg-slate-900 border-b border-slate-800 flex items-center justify-between gap-3 shrink-0">
                    <div className="relative flex-1 max-w-md">
                        <span className="absolute left-3 top-2.5 text-slate-500 material-symbols-outlined text-sm">search</span>
                        <input
                            type="text"
                            placeholder="Filter destination or source columns..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div className="text-xs text-slate-400">
                        Showing <strong className="text-white">{filteredMappings.length}</strong> of {mappings.length} mapping rules
                    </div>
                </div>

                {/* Main Mappings Table */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {loading ? (
                        <div className="h-48 flex flex-col items-center justify-center gap-3 text-slate-400">
                            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-xs">Introspecting live database columns and sample records...</span>
                        </div>
                    ) : filteredMappings.length === 0 ? (
                        <div className="text-center py-12 text-slate-500 text-sm">
                            No mapping rules match your search filter.
                        </div>
                    ) : (
                        filteredMappings.map((m, idx) => {
                            const originalIdx = mappings.indexOf(m);
                            const previewValue = getLivePreviewValue(m);
                            const isEnabled = m.enabled !== false;
                            const sourcesList = Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : (m.source ? [m.source] : []);
                            const isComposite = sourcesList.length > 1;

                            return (
                                <div
                                    key={m.target + '_' + idx}
                                    className={`p-3 rounded-xl border transition-all ${
                                        isEnabled
                                            ? 'bg-slate-850/80 border-slate-750 hover:border-slate-650 shadow-sm'
                                            : 'bg-slate-900/40 border-slate-800/60 opacity-60'
                                    }`}
                                >
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                                        {/* Target QuickBooks Field */}
                                        <div className="flex items-center gap-2.5 min-w-[240px]">
                                            <input
                                                type="checkbox"
                                                checked={isEnabled}
                                                onChange={() => handleToggleMapping(originalIdx)}
                                                className="w-4 h-4 rounded text-blue-600 bg-slate-800 border-slate-700 cursor-pointer"
                                            />
                                            <div>
                                                <div className="font-semibold text-white text-xs flex items-center gap-1.5">
                                                    <span>{m.target}</span>
                                                    {targetFields.find(tf => tf.name === m.target)?.required && (
                                                        <span className="text-rose-400 text-[10px] font-bold">*Required</span>
                                                    )}
                                                </div>
                                                <span className="text-[11px] text-slate-400">
                                                    {targetFields.find(tf => tf.name === m.target)?.label || 'QuickBooks Target Field'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Direction Arrow */}
                                        <div className="hidden lg:flex text-slate-500 text-xs items-center">
                                            <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                                        </div>

                                        {/* Source Column Pills (Supports Multi-Column Assignment) */}
                                        <div className="flex-1 min-w-[280px] space-y-1.5">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {sourcesList.map(srcCol => {
                                                    const sf = sourceFields.find(f => f.name.toLowerCase() === srcCol.toLowerCase());
                                                    return (
                                                        <span
                                                            key={srcCol}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-900 border border-sky-500/40 text-sky-200 font-mono text-xs shadow-sm"
                                                        >
                                                            <span className="font-semibold">{srcCol}</span>
                                                            {sf?.isPrimaryKey && (
                                                                <span className="bg-amber-500/20 text-amber-300 text-[9px] px-1 rounded">PK</span>
                                                            )}
                                                            {sourcesList.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveSourceColumn(originalIdx, srcCol)}
                                                                    className="text-slate-400 hover:text-rose-300 font-bold ml-1 text-xs"
                                                                    title="Remove column component"
                                                                >
                                                                    &times;
                                                                </button>
                                                            )}
                                                        </span>
                                                    );
                                                })}

                                                {/* Searchable Dropdown to Add Another Source Column */}
                                                <SearchableSelect
                                                    options={sourceSelectOptions}
                                                    value=""
                                                    onChange={(newCol) => handleAddSourceColumn(originalIdx, newCol)}
                                                    placeholder="+ Add Column..."
                                                    className="w-40"
                                                />

                                                {/* Delimiter Selector for Composite Columns */}
                                                {isComposite && (
                                                    <div className="flex items-center gap-1 bg-slate-900 px-2 py-1 rounded border border-slate-750 text-[11px] text-slate-300">
                                                        <span className="text-slate-400">Join:</span>
                                                        <select
                                                            value={m.delimiter || '-'}
                                                            onChange={(e) => handleDelimiterChange(originalIdx, e.target.value)}
                                                            className="bg-transparent text-sky-400 font-bold text-xs focus:outline-none cursor-pointer"
                                                        >
                                                            <option value="-">Hyphen (-)</option>
                                                            <option value=" ">Space ( )</option>
                                                            <option value=", ">Comma (, )</option>
                                                            <option value="/">Slash (/)</option>
                                                            <option value="">None (Concatenate)</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Live Evaluated Sample Value Preview */}
                                            <div className="flex items-center gap-2 text-[11px] font-mono">
                                                <span className="text-slate-500">Live Sample Result:</span>
                                                <span className="text-emerald-300 font-semibold px-2 py-0.5 rounded bg-emerald-950/40 border border-emerald-800/40 truncate max-w-sm">
                                                    {previewValue}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Action / Delete */}
                                        <div className="flex items-center justify-end">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveMapping(originalIdx)}
                                                className="text-slate-500 hover:text-rose-400 p-1.5 rounded hover:bg-slate-800 transition-colors"
                                                title="Remove this mapping rule"
                                            >
                                                <span className="material-symbols-outlined text-sm">delete</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}

                    {/* Add Custom Field Mapping Row */}
                    <div className="p-3 bg-slate-950/60 border border-dashed border-slate-750 rounded-xl space-y-2 mt-4">
                        <div className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[16px] text-blue-400">add_circle</span>
                            <span>Add Custom Field Mapping</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                            <div className="md:col-span-5">
                                <input
                                    type="text"
                                    placeholder="Target QB Field (e.g. Notes, CustomField.1)"
                                    value={newTarget}
                                    onChange={(e) => setNewTarget(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white"
                                />
                            </div>
                            <div className="md:col-span-5">
                                <SearchableSelect
                                    options={sourceSelectOptions}
                                    value={newSource}
                                    onChange={(col) => setNewSource(col)}
                                    placeholder="Search & select source column..."
                                    className="w-full"
                                />
                            </div>
                            <div className="md:col-span-2 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleAddCustomMapping}
                                    disabled={!newTarget.trim() || !newSource.trim()}
                                    className="w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-semibold disabled:opacity-40 transition-colors"
                                >
                                    + Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Bar */}
                <div className="p-4 border-t border-slate-800 bg-slate-950 flex justify-between items-center shrink-0">
                    <div className="text-xs text-slate-400">
                        {mappings.filter(m => m.enabled !== false).length} active mappings configured for <strong className="text-sky-300">{activeTableName}</strong> &rarr; <strong className="text-purple-300">{selectedQBEntity}</strong>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-xs text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-750 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={saving}
                            onClick={handleSave}
                            className="px-5 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded-lg shadow-lg shadow-blue-500/20 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                        >
                            {saving && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                            <span>Save Field Mappings</span>
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default FieldMappingModal;
